import {
  FunctionCallResult,
  Role,
  SendMessageResponse,
  SendMessageRequest,
} from '@wix/ambassador-genie-chat-service-v1-no-entity/build/cjs/types.impl';
import {
  DomainReferences,
  IChatServiceAmbassador,
  LiveChat,
  LiveChatMessages,
  SendUserMessageResult,
  ToolExecutionRegisry,
  ToolExecutionResult,
  ToolExecutionSpec,
} from '../types';

type ToolParsedParams = Record<string, unknown> & {
  _next_?: {
    toolName: string;
    payload: Record<string, unknown>;
    idx: number;
  };
};

const errorPrefix = 'invalid response from chat-service';

const NO_RESPONSE = '<no-response>' as const;

const safeError = (error: unknown) =>
  error instanceof Error ? error : new Error(JSON.stringify(error));

type SendChatServiceMessage = (
  msg: Pick<SendMessageRequest, 'userMessageContent' | 'functionCallResults'>,
  agentName: string | null,
) => Promise<SendMessageResponse & { requestId: string }>;

type GetTool = (toolName: string) => Promise<ToolExecutionSpec | undefined>;

class BasicChatMessagingManager implements ChatMessagingManager {
  constructor(
    public messages: LiveChatMessages,
    private readonly sendChatServiceMessage: SendChatServiceMessage,
    private readonly getTool: GetTool,
  ) {}

  async sendUserMessage(
    initialMessage: string,
    agentName: string,
  ): Promise<SendUserMessageResult> {
    const requestStartTime = Date.now();
    let _response = await this.sendMessage(initialMessage, agentName);
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (_response.ok === false) {
        return _response;
      }
      const { payload, fromAgent, requestId, logs = [] } = _response;
      if (typeof payload === 'string') {
        this.messages.push({
          role: Role.ASSISTANT,
          content: payload,
        });
        return { ok: true, content: payload, requestId, logs };
      }
      // response from agent-platform is a FunctionCall
      const functionCallResultBase = {
        id: payload.id ?? '',
        name: payload.name ?? '',
      };
      const { _next_, ...params } = JSON.parse(
        payload.arguments!,
      ) as ToolParsedParams;
      const toolId = _next_?.toolName || payload.name || 'NO-TOOL';
      const tool = await this.getTool(toolId);
      if (!tool) {
        _response = await this.sendMessage(
          {
            ...functionCallResultBase,
            success: false,
            content: `tool '${toolId}' not found and cannot be executed`,
          },
          fromAgent,
        );
        continue;
      }
      const request = {
        requestId: _response.requestId,
        responseTimeMs: Date.now() - requestStartTime,
        logs,
      };
      tool.beforeExecute?.(params, request);
      const result: ToolExecutionResult = await tool.execute(
        params,
        _next_?.payload ?? undefined,
      );
      if (_next_) {
        result.widgetResponse = JSON.stringify({
          ...params,
          _next_: {
            ..._next_,
            payload: JSON.parse(result.widgetResponse || '{}'),
          },
        });
      }
      _response = await this.sendMessage(
        {
          ...functionCallResultBase,
          ...result,
        },
        fromAgent,
      );
    }
  }

  private async sendMessage(
    message: string | FunctionCallResult,
    toAgent: string | null,
  ) {
    let data: SendMessageResponse & { requestId: string };
    try {
      const isUserMessage = typeof message === 'string';
      const msg = isUserMessage
        ? { userMessageContent: message }
        : { functionCallResults: [message] };
      data = await this.sendChatServiceMessage(msg, toAgent);
      if (isUserMessage) {
        this.messages.push({
          role: Role.USER,
          content: message,
        });
      }
    } catch (error: unknown) {
      return {
        ok: false as const,
        cause: 'send-message-error' as const,
        error: safeError(error),
      };
    }
    const { assistantMessages, functionCalls, logs, agentName, requestId } =
      data;
    if (assistantMessages?.length) {
      if (assistantMessages.length > 1) {
        throw new Error(`${errorPrefix}: more than one assistantMessages`);
      }
      const assistantMessage = assistantMessages.at(0);
      if (!assistantMessage) {
        throw new Error(
          `${errorPrefix}: assistantMessage is ${assistantMessage}`,
        );
      }
      return {
        payload: assistantMessages.at(0)?.content ?? NO_RESPONSE,
        fromAgent: agentName ?? 'master',
        requestId,
        logs,
      };
    }
    if (functionCalls?.length) {
      if (functionCalls.length > 1) {
        throw new Error(`${errorPrefix}: more than one functionCalls`);
      }
      const functionCall = functionCalls.at(0);
      if (!functionCall) {
        throw new Error(`${errorPrefix}: functionCall is ${functionCall}`);
      }
      return {
        payload: functionCall,
        fromAgent: agentName ?? 'master',
        requestId,
        logs,
      };
    }
    throw new Error(
      `${errorPrefix}: neither assistantMessages nor functionCalls`,
    );
  }
}

export type ChatMessagingManager = {
  readonly messages: LiveChatMessages;
  sendUserMessage: LiveChat['sendUserMessage'];
};

type CreateChatMessagingManagerParams = {
  sendMessage: IChatServiceAmbassador['sendMessage'];
  conversationId: string;
  domains: DomainReferences;
  toolsExecutorRegistry: ToolExecutionRegisry;
  initialMessages: LiveChatMessages;
  options: { enableLogging: boolean; noValidate: boolean };
};

export const createChatMessagingManager = ({
  sendMessage,
  conversationId,
  domains,
  toolsExecutorRegistry,
  initialMessages,
  options,
}: CreateChatMessagingManagerParams): ChatMessagingManager => {
  const sendChatServiceMessage = (
    msg: Pick<SendMessageRequest, 'userMessageContent' | 'functionCallResults'>,
    agentName: string | null,
  ) =>
    sendMessage({
      conversationId,
      agentName,
      domains: domains.map((domain) =>
        typeof domain === 'string' ? { domainId: domain } : domain,
      ),
      enableLogging: options.enableLogging,
      noValidate: options.noValidate,
      ...msg,
    });
  return new BasicChatMessagingManager(
    initialMessages,
    sendChatServiceMessage,
    (toolName: string) => Promise.resolve(toolsExecutorRegistry[toolName]),
  );
};
